import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { SectionContainer } from '@/components/shared'
import { websiteContent } from '../content'
import { usePanelViewTracking } from '../hooks/usePanelViewTracking'

const { approach } = websiteContent

const pillarVisuals = [
  {
    overlay: 'bg-[rgba(0,177,235,0.15)] backdrop-blur-[8px]',
    accent: 'text-[#00b1eb]',
    ghostColor: 'text-[rgba(0,177,235,0.1)]',
  },
  {
    overlay: 'bg-[rgba(253,142,46,0.15)] backdrop-blur-[8px]',
    accent: 'text-[#fd8e2e]',
    ghostColor: 'text-[rgba(253,142,46,0.1)]',
  },
  {
    overlay: 'bg-[rgba(255,105,88,0.15)] backdrop-blur-[8px]',
    accent: 'text-[#ff6958]',
    ghostColor: 'text-[rgba(255,105,88,0.1)]',
  },
] as const

const formatPillarTitle = (title: string) => {
  const words = title.trim().split(/\s+/)
  if (words.length <= 2) return words.join('\n')
  return `${words.slice(0, 2).join(' ')}\n${words.slice(2).join(' ')}`
}

const panelData = [
  {
    key: 'intro',
    label: '01. INTRO',
    title: approach.title,
    body: `${approach.subtitle} ${approach.description}`,
    overlay: 'bg-[rgba(20,20,20,0.08)] backdrop-blur-[14px]',
    accent: 'text-white',
    ghost: null as string | null,
    ghostColor: null as string | null,
  },
  ...approach.pillars.slice(0, 3).map((pillar, index) => ({
    key: `pillar-${index + 1}`,
    label: `${String(index + 2).padStart(2, '0')}. ${pillar.title.toUpperCase()}`,
    title: formatPillarTitle(pillar.title),
    body: pillar.description,
    overlay: pillarVisuals[index].overlay,
    accent: pillarVisuals[index].accent,
    ghost: pillar.title.toUpperCase(),
    ghostColor: pillarVisuals[index].ghostColor,
  })),
]

const panelVariants = {
  enter: (direction: number) => ({ opacity: 0, x: direction > 0 ? 80 : -80 }),
  center: { opacity: 1, x: 0 },
  exit: (direction: number) => ({ opacity: 0, x: direction > 0 ? -80 : 80 }),
}

function ApproachSection() {
  usePanelViewTracking({
    panelId: 'approach',
    panelName: 'Approach',
    panelIndex: 2,
  })

  const [activeIndex, setActiveIndex] = useState(0)
  const [direction, setDirection] = useState(0)
  const active = panelData[activeIndex]
  const isIntroPanel = active.key === 'intro'
  const mobileTextLayoutClass = isIntroPanel
    ? 'flex min-h-[735px] flex-col justify-center space-y-[56px] whitespace-pre-wrap'
    : 'flex h-[735px] flex-col justify-start overflow-y-auto overscroll-contain pr-1 pt-14 space-y-8 whitespace-pre-wrap'
  const desktopContentHeightClass = isIntroPanel
    ? 'h-[clamp(560px,72vh,946px)]'
    : 'h-[clamp(680px,82vh,946px)]'

  const handleSelect = (index: number) => {
    if (index === activeIndex) return
    setDirection(index > activeIndex ? 1 : -1)
    setActiveIndex(index)
  }

  return (
    <SectionContainer
      id="approach"
      size="page"
      className="relative min-h-[798px] overflow-hidden bg-gray-darkest text-white md:min-h-[clamp(760px,100vh,1056px)]"
      innerClassName="relative min-h-[798px] !px-0 md:min-h-[clamp(760px,100vh,1056px)]"
    >
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[#141414]/85" />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={active.key}
          className={`absolute inset-0 ${active.overlay}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        />
      </AnimatePresence>

      <div className="relative z-10 h-full">
        <div className="relative hidden min-h-[clamp(760px,100vh,1056px)] md:block">
          <div
            className={`absolute left-page-gutter right-page-gutter top-0 flex ${desktopContentHeightClass} flex-col justify-center xl:left-content-inset xl:right-content-inset 3xl:left-content-inset-wide 3xl:right-content-inset-wide`}
          >
            <div className={isIntroPanel ? 'max-w-[920px]' : 'max-w-[920px] xl:max-w-[980px] 3xl:max-w-[1080px]'}>
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={active.key}
                  custom={direction}
                  variants={panelVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.45, ease: 'easeOut' }}
                  className="space-y-[clamp(24px,4vw,56px)] whitespace-pre-wrap text-left"
                >
                  {isIntroPanel ? (
                    <>
                      <p className="font-archivo text-[12px] font-extralight uppercase leading-[16px] text-white">
                        {active.title}
                      </p>
                      <p className="text-[clamp(1.75rem,3.8vw,2.75rem)] leading-[clamp(2.25rem,5vw,3.75rem)] font-archivo font-extralight text-white">
                        {active.body}
                      </p>
                    </>
                  ) : (
                    <>
                      <p
                        className={`w-full text-[clamp(2.5rem,6vw,4.5rem)] font-archivo font-extralight leading-[1.05] ${active.accent}`}
                      >
                        {active.title}
                      </p>
                      <p className="w-full text-[clamp(1.5rem,3vw,2.75rem)] leading-[clamp(2rem,4.4vw,3.75rem)] font-archivo font-extralight text-white">
                        {active.body}
                      </p>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {active.ghost && active.ghostColor ? (
            <p
              className={`pointer-events-none absolute left-[clamp(12px,3vw,24px)] top-[clamp(24px,6vw,53px)] w-[min(95%,1896px)] whitespace-pre-wrap font-agency text-[clamp(10rem,20vw,25rem)] leading-[0.85] uppercase ${active.ghostColor}`}
            >
              {active.ghost}
            </p>
          ) : null}

          <div className="absolute bottom-[clamp(24px,4vw,40px)] left-[clamp(16px,3vw,24px)] right-[clamp(16px,3vw,24px)] z-40">
            <div className="grid grid-cols-4 gap-[clamp(12px,2vw,24px)]">
              {panelData.map((panel, index) => (
                <button
                  key={`bar-${panel.key}`}
                  type="button"
                  onClick={() => handleSelect(index)}
                  className={`h-px w-full cursor-pointer ${
                    index === activeIndex ? 'bg-white' : 'bg-white/50'
                  }`}
                  aria-label={panel.label}
                />
              ))}
            </div>
            <div className="mt-4 grid grid-cols-4 gap-[clamp(12px,2vw,24px)] bg-transparent">
              {panelData.map((panel, index) => (
                <button
                  key={`label-${panel.key}`}
                  type="button"
                  onClick={() => handleSelect(index)}
                  className={`w-full !bg-transparent text-left text-caption font-archivo font-light uppercase tracking-[0.24px] ${
                    index === activeIndex ? 'text-white' : 'text-white/50'
                  } cursor-pointer`}
                >
                  {panel.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="relative min-h-[798px] px-10 pb-6 pt-0 md:hidden">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={`mobile-${active.key}`}
              custom={direction}
              variants={panelVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className={mobileTextLayoutClass}
            >
              {isIntroPanel ? (
                <>
                  <p className="font-archivo text-[12px] font-extralight uppercase leading-[16px] text-white">
                    {active.title}
                  </p>
                  <p className="text-[32px] leading-[46px] font-archivo font-extralight text-white">
                    {active.body}
                  </p>
                </>
              ) : (
                <>
                  <p
                    className={`text-[52px] font-archivo font-extralight leading-[56px] ${active.accent}`}
                  >
                    {active.title}
                  </p>
                  <p className="text-[32px] leading-[46px] font-archivo font-extralight text-white">
                    {active.body}
                  </p>
                </>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="absolute bottom-[24px] left-[24px] right-[24px]">
            <div className="grid grid-cols-4 gap-4">
              {panelData.map((panel, index) => (
                <button
                  key={`mobile-bar-${panel.key}`}
                  type="button"
                  onClick={() => handleSelect(index)}
                  className="relative h-11 w-full cursor-pointer appearance-none !border-0 !bg-transparent p-0 shadow-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/70"
                  aria-label={panel.label}
                  style={{ backgroundColor: 'transparent' }}
                >
                  <span
                    aria-hidden="true"
                    className={`absolute bottom-0 left-0 right-0 h-px ${
                      index === activeIndex ? 'bg-white' : 'bg-gray-lightest/50'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="mt-4 text-caption font-archivo font-light text-white">
              {panelData[activeIndex].label}
            </p>
          </div>
        </div>
      </div>
    </SectionContainer>
  )
}

export default ApproachSection
